import { useRef, useState, type CSSProperties, type PointerEvent } from 'react';
import styles from '../Disco.module.css';
import { useTheme } from '../../../../../../hooks/useTheme';
import Button from '../../../../../ui/Button/BlockButton';
import { AdContent } from './AdContent';
import { StoryBars } from './StoryBars';
import type { BannerNavControl, BannerNavigation } from '../bannerFormat';
import type { DiscoOffer } from '../types';

export const PILL_BUTTON_STYLE: CSSProperties = {
  borderRadius: '999px',
  textTransform: 'none',
  letterSpacing: 'normal',
};

const SWIPE_PX = 32;

export interface BannerOfferContent {
  offerId: string;
  heading: string;
  subheading: string;
  disclosureDetails?: DiscoOffer['disclosure_details'];
  jsonContent?: unknown;
  advertiserName: string;
  logoUrl?: string;
  heroImageUrl?: string;
  acceptCopy: string;
  declineCopy: string;
}

interface BannerOfferProps {
  offer: BannerOfferContent;
  navigation: BannerNavigation;
  autoAdvance: boolean;
  index: number;
  total: number;
  onPrev: (control: BannerNavControl) => void;
  onNext: (control: BannerNavControl) => void;
  onAutoAdvance: () => void;
  onAccept: () => void;
  onDecline: () => void;
  widgetRef: React.RefCallback<HTMLDivElement>;
  adRef: React.RefCallback<HTMLDivElement>;
}

export function BannerOffer({
  offer,
  navigation,
  autoAdvance,
  index,
  total,
  onPrev,
  onNext,
  onAutoAdvance,
  onAccept,
  onDecline,
  widgetRef,
  adRef,
}: BannerOfferProps) {
  const theme = useTheme();
  const [expandedOfferId, setExpandedOfferId] = useState<string | null>(null);
  const swipeStartX = useRef<number | null>(null);
  const isStory = navigation === 'story';
  const isFirst = index === 0;
  const isLast = index >= total - 1;
  const expanded = expandedOfferId === offer.offerId;

  const handlePointerDown = (e: PointerEvent<HTMLDivElement>) => {
    swipeStartX.current = e.clientX;
    // Listen on the window, not the card, so a swipe that ends outside the
    // card still counts. No pointer capture, so links and buttons inside the
    // card keep receiving their own clicks.
    const onEnd = (ev: globalThis.PointerEvent) => {
      window.removeEventListener('pointerup', onEnd);
      window.removeEventListener('pointercancel', onEnd);
      const startX = swipeStartX.current;
      swipeStartX.current = null;
      if (ev.type !== 'pointerup' || startX === null) return;
      const dx = ev.clientX - startX;
      if (Math.abs(dx) < SWIPE_PX) return;
      if (dx < 0 && !isLast) onNext('swipe');
      if (dx > 0 && !isFirst) onPrev('swipe');
    };
    window.addEventListener('pointerup', onEnd);
    window.addEventListener('pointercancel', onEnd);
  };

  return (
    <div
      ref={widgetRef}
      className={styles.banner}
      style={{ fontFamily: theme.bodytextDefaultNone?.fontFamily }}
    >
      <div
        ref={adRef}
        data-testid="banner-swipe-area"
        className={styles.bannerSwipeArea}
        onPointerDown={handlePointerDown}
      >
        <StoryBars
          index={index}
          total={total}
          autoAdvance={isStory && autoAdvance && !isLast}
          onComplete={onAutoAdvance}
        />
        <div className={styles.brandBlock}>
          {offer.logoUrl && (
            <div className={styles.brandLogo}>
              <img src={offer.logoUrl} alt="" />
            </div>
          )}
          <span className={styles.brandName}>{offer.advertiserName}</span>
          <span className={styles.sponsoredBadge}>Sponsored</span>
        </div>
        <div className={styles.bannerCopy}>
          <h2
            className={styles.bannerTitle}
            style={{
              fontFamily: theme.headersMediumNone?.fontFamily,
              color: theme.headersMediumNone?.color,
            }}
          >
            {offer.heading}
          </h2>
          <div
            data-testid="banner-body"
            className={expanded ? undefined : styles.bannerBodyClamped}
            onClick={
              expanded ? undefined : () => setExpandedOfferId(offer.offerId)
            }
          >
            <AdContent
              subheading={offer.subheading}
              disclosureDetails={offer.disclosureDetails}
              jsonContent={offer.jsonContent}
              bodyStyle={theme.bodytextDefaultNone}
            />
          </div>
        </div>
      </div>
      <div className={styles.pillActions}>
        <Button big onClick={onAccept}>
          {offer.acceptCopy}
        </Button>
        <Button big outline onClick={onDecline}>
          {offer.declineCopy}
        </Button>
      </div>
    </div>
  );
}
